import { parseSocialEmbedUrl, type SocialEmbed } from "@/lib/social-embeds";
import { parseYouTubeVideoUrl } from "@/lib/youtube-video";
import { parseExternalEmbed, UNSUPPORTED_EXTERNAL_EMBED_ERROR, type ExternalEmbed } from "@/lib/external-embed";

export type ArticleGalleryLayout = "normal" | "wide" | "full";
export type ArticleGalleryAspectRatio = "16:9" | "4:3" | "1:1";
export type ArticleGalleryObjectFit = "cover" | "contain";

export type GalleryVideoRender =
  | { kind: "direct"; src: string }
  | { kind: "youtube"; src: string; videoId: string }
  | { kind: "social"; embed: SocialEmbed }
  | { kind: "unsupported"; src: string };
export type GalleryEmbedRender = { kind: "embed"; embed: ExternalEmbed } | { kind: "unsupported"; src: string };

const DIRECT_VIDEO_EXTENSION_PATTERN = /\.(mp4|webm|mov)(?:[?#].*)?$/i;
const IMAGE_EXTENSION_PATTERN = /\.(jpe?g|png|webp|gif|avif)(?:[?#].*)?$/i;
const VERCEL_BLOB_HOST_PATTERN = /(?:^|\.)blob\.vercel-storage\.com$/i;
const TRUSTED_GALLERY_IFRAME_HOSTS = new Set(["youtube.com", "www.youtube.com", "youtu.be", "youtube-nocookie.com", "www.youtube-nocookie.com"]);
export const GALLERY_UNSUPPORTED_VIDEO_URL_ERROR = "Nem támogatott videó URL. YouTube linket, iframe kódot vagy közvetlen videófájlt adj meg.";
export const GALLERY_UNSUPPORTED_EMBED_ERROR = UNSUPPORTED_EXTERNAL_EMBED_ERROR;

type GalleryValidationItem = {
  type?: unknown;
  url?: unknown;
  embedHtml?: unknown;
  embedUrl?: unknown;
  title?: unknown;
  caption?: unknown;
  description?: unknown;
};

export type GalleryValidationIssue = {
  path: string;
  message: string;
  field?: "url" | "text" | "items";
};

function decodeHtmlEntities(input: string) {
  return input
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, "\"")
    .replace(/&#39;/gi, "'")
    .replace(/&apos;/gi, "'")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">");
}

function extractIframeSrc(input: string) {
  if (!/<iframe[\s>]/i.test(input)) return null;
  const match = input.match(/<iframe\b[^>]*\bsrc\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i);
  return match ? decodeHtmlEntities(match[1] ?? match[2] ?? match[3] ?? "").trim() : "";
}

function isTrustedGalleryIframeSrc(input: string) {
  try {
    const url = new URL(input);
    if (url.protocol !== "https:" && url.protocol !== "http:") return false;
    return TRUSTED_GALLERY_IFRAME_HOSTS.has(url.hostname.toLowerCase());
  } catch {
    return false;
  }
}

function parseYouTubeStartSeconds(input: string) {
  try {
    const url = new URL(input);
    const rawStart = url.searchParams.get("start") ?? url.searchParams.get("t");
    if (!rawStart) return null;
    const plainSeconds = Number(rawStart.replace(/s$/i, ""));
    if (Number.isFinite(plainSeconds) && plainSeconds >= 0) return Math.floor(plainSeconds);
    const timeMatch = rawStart.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s?)?$/i);
    if (!timeMatch) return null;
    const hours = Number(timeMatch[1] ?? 0);
    const minutes = Number(timeMatch[2] ?? 0);
    const seconds = Number(timeMatch[3] ?? 0);
    const total = (hours * 3600) + (minutes * 60) + seconds;
    return total > 0 ? total : null;
  } catch {
    return null;
  }
}

function youtubeGalleryEmbedUrl(input: string) {
  const youtube = parseYouTubeVideoUrl(input);
  if (!youtube) return null;
  const embedUrl = new URL(youtube.embedUrl);
  const start = parseYouTubeStartSeconds(input);
  if (start && start > 0) embedUrl.searchParams.set("start", String(start));
  return { src: embedUrl.toString(), videoId: youtube.videoId };
}

export function normalizeGalleryVideoInput(input: string) {
  const trimmed = input.trim();
  if (!trimmed) return "";

  const iframeSrc = extractIframeSrc(trimmed);
  if (iframeSrc !== null) {
    if (!iframeSrc || !isTrustedGalleryIframeSrc(iframeSrc)) return "";
    return youtubeGalleryEmbedUrl(iframeSrc)?.src ?? "";
  }

  const decoded = decodeHtmlEntities(trimmed);
  return youtubeGalleryEmbedUrl(decoded)?.src ?? decoded;
}

export function directGalleryVideoUrl(input: string) {
  const rawUrl = input.trim();
  if (!rawUrl) return null;
  try {
    const url = new URL(rawUrl);
    if (url.protocol !== "https:" && url.protocol !== "http:") return null;
    if (DIRECT_VIDEO_EXTENSION_PATTERN.test(url.pathname)) return rawUrl;
    return VERCEL_BLOB_HOST_PATTERN.test(url.hostname) ? rawUrl : null;
  } catch {
    return null;
  }
}

export function isHttpUrl(input: unknown) {
  if (typeof input !== "string" || !input.trim()) return false;
  try {
    const url = new URL(input.trim());
    return url.protocol === "https:" || url.protocol === "http:";
  } catch {
    return false;
  }
}

export function isSupportedGalleryVideoUrl(input: string) {
  const render = galleryVideoRender(input);
  return Boolean(render && render.kind !== "unsupported");
}

export function isSupportedGalleryImageUrl(input: string) {
  const rawUrl = input.trim();
  if (!rawUrl || !isHttpUrl(rawUrl)) return false;
  if (parseExternalEmbed(rawUrl)) return false;
  try {
    const url = new URL(rawUrl);
    if (IMAGE_EXTENSION_PATTERN.test(url.pathname) || VERCEL_BLOB_HOST_PATTERN.test(url.hostname)) return true;
    return !parseYouTubeVideoUrl(rawUrl) && !parseSocialEmbedUrl(rawUrl) && !directGalleryVideoUrl(rawUrl);
  } catch {
    return false;
  }
}

export function galleryVideoRender(input: string): GalleryVideoRender | null {
  const rawUrl = normalizeGalleryVideoInput(input);
  if (!rawUrl) return null;

  const youtube = youtubeGalleryEmbedUrl(rawUrl);
  if (youtube) {
    return { kind: "youtube", src: youtube.src, videoId: youtube.videoId };
  }

  const social = parseSocialEmbedUrl(rawUrl);
  if (social) return { kind: "social", embed: social };

  const direct = directGalleryVideoUrl(rawUrl);
  if (direct) return { kind: "direct", src: direct };

  return { kind: "unsupported", src: rawUrl };
}

export function galleryYouTubeThumbnailUrl(videoId: string) {
  return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
}

export function safeGalleryLayout(input: unknown): ArticleGalleryLayout {
  return input === "normal" || input === "full" ? input : "wide";
}

export function safeGalleryAspectRatio(input: unknown): ArticleGalleryAspectRatio {
  return input === "4:3" || input === "1:1" ? input : "16:9";
}

export function safeGalleryObjectFit(input: unknown): ArticleGalleryObjectFit {
  return input === "contain" ? "contain" : "cover";
}

export function safeGalleryFocalPercent(input: unknown) {
  const value = Number(input);
  if (!Number.isFinite(value)) return 50;
  return Math.max(0, Math.min(100, Math.round(value)));
}

export function safeGalleryZoom(input: unknown) {
  const value = Number(input);
  if (!Number.isFinite(value)) return 1;
  return Math.max(1, Math.min(3, Math.round(value * 100) / 100));
}

export function galleryAspectRatioCss(aspectRatio: ArticleGalleryAspectRatio) {
  if (aspectRatio === "4:3") return "4 / 3";
  if (aspectRatio === "1:1") return "1 / 1";
  return "16 / 9";
}

export function safeGallerySourceUrl(input: unknown) {
  if (typeof input !== "string" || !input.trim()) return "";
  const value = input.trim();
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:" ? url.toString() : "";
  } catch {
    return "";
  }
}

export function gallerySourceFallbackLabel(input: string) {
  try {
    const hostname = new URL(input).hostname.replace(/^www\./, "");
    return hostname || "Forrás";
  } catch {
    return "Forrás";
  }
}

export function galleryItemType(input: unknown): "image" | "video" | "text" | "embed" {
  return input === "video" ? "video" : input === "text" ? "text" : input === "embed" ? "embed" : "image";
}

export function galleryEmbedInput(item: Pick<GalleryValidationItem, "url" | "embedUrl" | "embedHtml">) {
  return typeof item.embedHtml === "string" && item.embedHtml.trim()
    ? item.embedHtml.trim()
    : typeof item.embedUrl === "string" && item.embedUrl.trim()
      ? item.embedUrl.trim()
      : typeof item.url === "string"
        ? item.url.trim()
        : "";
}

export function galleryEmbedRender(input: string): GalleryEmbedRender | null {
  const rawInput = input.trim();
  if (!rawInput) return null;
  const embed = parseExternalEmbed(rawInput);
  return embed ? { kind: "embed", embed } : { kind: "unsupported", src: rawInput };
}

export function isSupportedGalleryEmbed(input: string) {
  const render = galleryEmbedRender(input);
  return Boolean(render && render.kind !== "unsupported");
}

function meaningfulString(input: unknown) {
  return typeof input === "string" && input.trim().length > 0;
}

export function validateGalleryItem(item: GalleryValidationItem, path: string): GalleryValidationIssue[] {
  const type = galleryItemType(item.type);
  const url = typeof item.url === "string" ? item.url.trim() : "";
  const issues: GalleryValidationIssue[] = [];

  if (type === "image") {
    if (!url) {
      issues.push({ path, field: "url", message: "Kép URL szükséges." });
    } else if (!isSupportedGalleryImageUrl(url)) {
      issues.push({ path, field: "url", message: "Nem támogatott kép URL." });
    }
  } else if (type === "video") {
    if (!url) {
      issues.push({ path, field: "url", message: "Videó URL szükséges." });
    } else if (!isSupportedGalleryVideoUrl(url)) {
      issues.push({ path, field: "url", message: GALLERY_UNSUPPORTED_VIDEO_URL_ERROR });
    }
  } else if (type === "embed") {
    const embedInput = galleryEmbedInput(item);
    if (!embedInput) {
      issues.push({ path, field: "url", message: "Embed URL vagy iframe kód szükséges." });
    } else if (!isSupportedGalleryEmbed(embedInput)) {
      issues.push({ path, field: "url", message: GALLERY_UNSUPPORTED_EMBED_ERROR });
    }
  } else if (!meaningfulString(item.title) && !meaningfulString(item.caption) && !meaningfulString(item.description)) {
    issues.push({ path, field: "text", message: "Szöveges galériaelemhez cím, képaláírás vagy szöveg szükséges." });
  }

  return issues;
}

export function galleryValidationIssueLabel(issue: GalleryValidationIssue) {
  return `${issue.path}: ${issue.message}`;
}

export function collectGalleryValidationIssues(contentBlocks: unknown): GalleryValidationIssue[] {
  if (
    typeof contentBlocks !== "object" ||
    contentBlocks === null ||
    (contentBlocks as { type?: unknown }).type !== "lexical"
  ) {
    return [];
  }

  const issues: GalleryValidationIssue[] = [];
  let galleryIndex = 0;

  function visit(node: unknown) {
    if (typeof node !== "object" || node === null) return;
    const current = node as { type?: unknown; items?: unknown[]; children?: unknown[] };
    if (current.type === "gallery") {
      const currentGalleryIndex = galleryIndex;
      galleryIndex += 1;
      if (!Array.isArray(current.items) || current.items.length === 0) {
        issues.push({ path: `Galéria ${currentGalleryIndex + 1}. blokk`, field: "items", message: "Elem hozzáadása szükséges." });
      } else {
        current.items.forEach((rawItem, itemIndex) => {
          const item = typeof rawItem === "object" && rawItem !== null ? rawItem as GalleryValidationItem : {};
          issues.push(...validateGalleryItem(item, `Galéria ${currentGalleryIndex + 1}. blokk, ${itemIndex + 1}. elem`));
        });
      }
    }
    current.children?.forEach(visit);
  }

  visit((contentBlocks as { editorState?: { root?: unknown } }).editorState?.root);
  return issues;
}
